import { useId, type ReactNode } from "react";

/**
 * A circular progress arc with room in the middle. Track is #f3f3f5; the
 * progress stroke is the Pact pastel gradient, starting at 12 o'clock.
 * `disc` adds the white backing and soft shadow the Today screen uses.
 */
export function Ring({
  fraction,
  size = 56,
  stroke = 3,
  disc = false,
  children,
}: {
  fraction: number;
  size?: number;
  stroke?: number;
  disc?: boolean;
  children?: ReactNode;
}) {
  const gradientId = useId();
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(1, Math.max(0, fraction));

  return (
    <div
      className="relative shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        ...(disc ? { background: "#fff", boxShadow: "0 6px 20px rgba(8,8,10,0.08)" } : null),
      }}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="oklch(0.84 0.08 240)" />
            <stop offset="0.5" stopColor="oklch(0.86 0.08 195)" />
            <stop offset="1" stopColor="oklch(0.87 0.1 155)" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f3f3f5"
          strokeWidth={stroke}
        />
        {clamped > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - clamped)}
            className="transition-[stroke-dashoffset] duration-200 ease-out motion-reduce:transition-none"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}
